<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Loader from '@/components/loader/Loader.vue';
import { Badge } from '@/components/ui/badge';
import PromptStatusBadge from '@/components/output/PromptStatusBadge.vue';
import copy from 'copy-to-clipboard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ClipboardCopyIcon } from 'lucide-vue-next';
import { PromptService } from '@/services/PromptService.ts';
import { ROUTES } from '@/router.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import * as smd from 'streaming-markdown';
import type { Default_Renderer, Parser } from 'streaming-markdown';
import {
    eventEmitter,
    type StreamContentPayload,
    type StreamFinishedPayload,
    type StreamStartedPayload,
} from '@/lib/eventEmitter.ts';
import PromptEditButton from '@/components/output/PromptEditButton.vue';

const props = defineProps<{
    sessionId: string;
    profileConfig: ProfileConfig;
    stepConfig: StepConfig;
    promptConfig: PromptConfig;
    promptIndex: number;
    streamId: string;
    content: string;
    selectedNavigationIndex: number;
    variableChainHash: string;
    isPromptSlow: boolean;
}>();

const sessionStore = useSessionStore();

const markdownOutputElement = ref<HTMLDivElement | null>(null);
const hasContent = ref(false);

let renderer: Default_Renderer;
let parser: Parser;

const setupRenderer = () => {
    if (!markdownOutputElement.value) {
        throw new Error('Markdown output element not found');
    }
    renderer = smd.default_renderer(markdownOutputElement.value);
    parser = smd.parser(renderer);
};

/* region Computed */
const promptStatus = computed(() => {
    return PromptService.getPromptStatus(
        props.promptConfig.id,
        props.sessionId,
        props.variableChainHash,
    );
});
/* endregion Computed */

onMounted(() => {
    setupRenderer();

    if (props.content !== '') {
        smd.parser_write(parser, props.content);
        hasContent.value = true;
    }

    eventEmitter.on('streamStarted', (payload: StreamStartedPayload) => {
        if (payload.streamId !== props.streamId) {
            return;
        }
        hasContent.value = true;
    });

    eventEmitter.on('streamContent', (payload: StreamContentPayload) => {
        if (payload.streamId !== props.streamId) {
            return;
        }
        smd.parser_write(parser, payload.deltaContent);
        hasContent.value = true;
    });

    eventEmitter.on('streamFinished', (payload: StreamFinishedPayload) => {
        if (payload.streamId !== props.streamId) {
            return;
        }
        smd.parser_end(parser);
        hasContent.value = true;
    });
});
/* endregion Lifecycle */
</script>

<template>
    <Card>
        <CardHeader class="px-4 py-2 pe-2 text-lg">
            <CardTitle>
                <div class="flex items-center justify-between text-base">
                    {{ promptConfig.name }}
                    <div class="flex items-center gap-x-1">
                        <PromptStatusBadge
                            :prompt-config-id="promptConfig.id"
                            :session-id="sessionId"
                            :variable-chain-hash="variableChainHash"
                        />
                        <PromptEditButton
                            v-if="!sessionStore.isInsideWebView"
                            :profile-config-id="profileConfig.id"
                            :step-config-id="stepConfig.id"
                            :prompt-config-id="promptConfig.id"
                        />
                    </div>
                </div>
            </CardTitle>
        </CardHeader>
        <CardContent class="relative p-0 dark:bg-zinc-900/60">
            <div v-show="hasContent">
                <TooltipProvider :delay-duration="150">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Badge
                                variant="secondary"
                                class="absolute right-1.5 top-1.5 rounded-sm bg-transparent text-xs"
                                @click="copy(props.content)"
                                ><ClipboardCopyIcon class="h-4 w-4"
                            /></Badge>
                        </TooltipTrigger>
                        <TooltipContent :side="'left'">
                            <p>Copy to clipboard</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <div ref="markdownOutputElement" class="markdown-output"></div>
            </div>

            <template v-if="!hasContent">
                <div
                    v-if="promptStatus.status === 'error'"
                    class="flex items-center justify-center p-5"
                >
                    Failed to retrieve output, please try again.
                </div>
                <div class="flex flex-col items-center justify-center p-5">
                    <Loader :width="24" :height="24" />
                    <div
                        v-if="isPromptSlow && promptStatus.status !== 'streaming'"
                        class="mt-4 max-w-md text-center text-sm"
                    >
                        <template v-if="!sessionStore.isInsideWebView">
                            The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                            >) is currently taking a long time to respond. We recommend temporarily
                            <RouterLink
                                v-if="promptConfig.editable"
                                :to="{
                                    name: ROUTES.editUpdate,
                                    params: { profileConfigId: profileConfig.id },
                                }"
                                class="text-blue-600 hover:underline"
                                >switching</RouterLink
                            >
                            <RouterLink
                                v-else
                                :to="{
                                    name: ROUTES.editIndex,
                                }"
                                class="text-blue-600 hover:underline"
                                >switching</RouterLink
                            >
                            to another model.
                        </template>
                        <template v-else>
                            The selected AI model (<strong>{{ promptConfig.modelId }}</strong
                            >) is currently taking a long time to respond. We recommend temporarily
                            switching to another model.
                        </template>
                    </div>
                </div>
            </template>
        </CardContent>
    </Card>
</template>

<style scoped>
.markdown-output {
    @apply prose prose-sm prose-slate w-full max-w-full p-4 dark:prose-invert;
    font-size: 0.9rem;
    user-select: text !important;
}

.markdown-output * {
    user-select: text !important;
}

.markdown-output > *:first-child,
.markdown-output ul li:first-child p {
    margin-top: 0 !important;
}

.markdown-output > *:last-child,
.markdown-output ul li:last-child p {
    margin-bottom: 0 !important;
}

.markdown-output ul li p {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}
</style>
